type StorageType = "local" | "session";

const storageAvailability: Record<StorageType, boolean | null> = {
  local: null,
  session: null,
};

const resolveStorage = (type: StorageType): Storage | null => {
  if (typeof window === "undefined") return null;
  return type === "local" ? window.localStorage : window.sessionStorage;
};

const isStorageAvailable = (type: StorageType): boolean => {
  if (storageAvailability[type] !== null) {
    return storageAvailability[type] as boolean;
  }

  try {
    const storage = resolveStorage(type);
    if (!storage) {
      storageAvailability[type] = false;
      return false;
    }

    const probeKey = "__storage_probe__";
    storage.setItem(probeKey, "1");
    storage.removeItem(probeKey);
    storageAvailability[type] = true;
    return true;
  } catch (error) {
    console.error(`Storage ${type} is not available:`, error);
    storageAvailability[type] = false;
    return false;
  }
};

const getItem = (key: string, type: StorageType): string | null => {
  if (!isStorageAvailable(type)) return null;

  try {
    const storage = resolveStorage(type);
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const setItem = (key: string, value: string, type: StorageType): boolean => {
  if (!isStorageAvailable(type)) return false;

  try {
    const storage = resolveStorage(type);
    storage?.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const removeItem = (key: string, type: StorageType): boolean => {
  if (!isStorageAvailable(type)) return false;

  try {
    const storage = resolveStorage(type);
    storage?.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

export const safeStorage = {
  isStorageAvailable,
  getLocalItem: (key: string) => getItem(key, "local"),
  getSessionItem: (key: string) => getItem(key, "session"),
  setLocalItem: (key: string, value: string) => setItem(key, value, "local"),
  setSessionItem: (key: string, value: string) =>
    setItem(key, value, "session"),
  removeLocalItem: (key: string) => removeItem(key, "local"),
  removeSessionItem: (key: string) => removeItem(key, "session"),
};
